import React, { FC } from "react";
import { useHistory, useLocation } from "react-router";
import classnames from "classnames";
import SubMenu from "./sub-menu";
import { MenuItem } from "../../../../constants";
import { AuthService } from "../../../../services";
import { LogoutIcon, SidebarToggleIcon } from "../../../../components/icons";

interface IMenuNavProps {
  sidebarCollapsed: boolean;
  navCollapsed: boolean;
  onToggleNav(): void;
  menus: MenuItem[];
  activeMenu?: MenuItem;
  onSelectMenu(menu: MenuItem): void;
  onTogglePin(item: MenuItem): void;
  onToggleSidebar(): void;
}

// Shared focus treatment. focus-visible (not focus) so a mouse click does not
// leave a ring behind, while tab navigation stays clearly traceable.
const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1";

const MenuNav: FC<IMenuNavProps> = ({
  sidebarCollapsed,
  navCollapsed,
  onToggleNav,
  menus,
  activeMenu,
  onSelectMenu,
  onTogglePin,
  onToggleSidebar,
}) => {
  const history = useHistory();
  const location = useLocation();

  const onMenuClick = (menu: MenuItem) => {
    if (menu.type !== "menu") {
      return;
    }
    // NB the `menu !== activeMenu` guard this replaces never fired, so
    // selecting unconditionally preserves the behaviour that actually shipped.
    // The reason has changed: Sidebar used to build activeMenu as
    // `{...menu, subMenus}`, a fresh object that could never be
    // reference-equal to an entry in `menus`. It now derives it from the same
    // pruned array, so the two ARE the same reference and a restored guard
    // would suddenly fire -- which would be a design change (click-to-deselect),
    // not a fix.
    onSelectMenu(menu);
    if (menu.link) {
      history.push(menu.link);
    }
  };

  const onLogout = () => {
    AuthService.logout();
  };

  return (
    <nav
      aria-label="Main"
      className={classnames(
        "bg-white py-2 flex flex-col justify-between overflow-x-hidden md:overflow-x-visible transition-all",
        // max-md:invisible, not width alone. Below md this <nav> IS the
        // drawer, and w-0 with overflow-x-hidden clips it visually while
        // leaving every button in the tab order and the accessibility tree --
        // so a keyboard or screen-reader user walked the whole nav for a
        // drawer that is shut. visibility:hidden removes it from both and
        // still animates, which display:none would not.
        sidebarCollapsed ? "w-0 max-md:invisible" : "w-58",
        navCollapsed ? "md:w-18" : "md:w-58"
      )}
    >
      <div>
        {/* Rail toggle. Hidden below md, where the sidebar is an overlay drawer
            driven by the header's own control rather than an inline rail. */}
        <div
          className={classnames(
            "hidden md:flex px-3 pb-1",
            navCollapsed ? "justify-center" : "justify-end"
          )}
        >
          <button
            type="button"
            onClick={onToggleNav}
            aria-expanded={!navCollapsed}
            aria-label={navCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={navCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={classnames(
              "p-2 rounded-md text-gray-light hover:bg-gray-f8 hover:text-primary transition-all",
              FOCUS_RING
            )}
          >
            <SidebarToggleIcon size={18} color="currentColor" />
          </button>
        </div>

        {menus.map((menu: MenuItem, index: number) => {
          const menuId =
            menu.type === "menu"
              ? `menu-${menu.text.replace(/\W/g, "-").toLowerCase()}`
              : undefined;
          const isActive =
            menu.text === activeMenu?.text ||
            (!activeMenu &&
              menu.type === "menu" &&
              location.pathname.startsWith(menu.prefix));
          const hasSubMenu = !!menu.subMenus?.length;
          return (
            <div key={index} id={menuId} data-cy={menuId} className="px-3">
              <button
                type="button"
                onClick={() => onMenuClick(menu)}
                aria-current={isActive ? "page" : undefined}
                // Not `&& !navCollapsed`: that is a desktop-only preference,
                // and below md the sub-menu is on screen regardless. Whether a
                // breakpoint then hides it is presentation, not state.
                aria-expanded={hasSubMenu ? isActive : undefined}
                title={navCollapsed ? menu.text : undefined}
                className={classnames(
                  "w-full flex items-center cursor-pointer space-x-2 py-3 rounded-md transition-all",
                  navCollapsed ? "md:justify-center md:px-0 px-4" : "px-4",
                  isActive
                    ? "bg-primary-light"
                    : "bg-white hover:bg-gray-f8",
                  FOCUS_RING
                )}
              >
                {menu.icon &&
                  menu.icon({ color: !isActive ? "#A8AEB7" : "primary" })}
                <span
                  className={classnames(
                    "text-md text-left",
                    // Collapsed hides the label but keeps the icon and the
                    // active pill, so the current section stays identifiable.
                    navCollapsed ? "md:hidden" : "",
                    isActive
                      ? "text-primary font-semibold"
                      : "text-blue-dark font-normal"
                  )}
                >
                  {menu.text}
                </span>
              </button>
              {/* Scoped with md:hidden rather than gated in JSX, matching the
                  label span above. `navCollapsed` is the DESKTOP rail
                  preference and it persists across reloads; gating here applied
                  it at every breakpoint, so collapsing the rail on a laptop and
                  later opening the drawer on a phone left every top-level item
                  with no sub-navigation -- and the control that would undo it is
                  `hidden md:flex`, so there was no way back. */}
              <div
                data-testid={`submenu-wrap-${menu.text
                  .replace(/\W/g, "-")
                  .toLowerCase()}`}
                className={classnames("ml-6", navCollapsed ? "md:hidden" : "")}
              >
                {isActive && (
                  <SubMenu
                    sidebarCollapsed={sidebarCollapsed}
                    menu={menu}
                    onTogglePin={onTogglePin}
                    onToggleSidebar={onToggleSidebar}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={classnames(
          "px-4 space-y-4",
          navCollapsed ? "md:px-0 md:flex md:flex-col md:items-center" : ""
        )}
      >
        <button
          type="button"
          onClick={onLogout}
          title={navCollapsed ? "Log out" : undefined}
          aria-label="Log out"
          className={classnames(
            "flex items-center text-blue-dark cursor-pointer text-md font-semibold rounded-md hover:text-primary transition-all",
            navCollapsed ? "md:p-2" : "",
            FOCUS_RING
          )}
        >
          {/* Wrapped rather than classed directly: Icon already sets
              `inline-flex`, so a `hidden` passed through className would rely on
              Tailwind's display-utility ordering to win. */}
          {navCollapsed && (
            <span className="hidden md:inline-flex">
              <LogoutIcon size={18} color="currentColor" />
            </span>
          )}
          <span className={navCollapsed ? "md:hidden" : ""}>Log out</span>
        </button>
        <div className={classnames("text-2xs mt-1", navCollapsed ? "md:hidden" : "")}>
          V 1.1.1
        </div>
      </div>
    </nav>
  );
};

export default MenuNav;
